import get from 'lodash/get';
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { SingleSelect } from '@els/els-react--select';
import TreeView from '@Containers/TreeView';
import { TAXONOMY_BOUND_OPERATORS } from '../constants';
import TaxonomyLookupModalSearch from './TaxonomyLookupModalSearch';

import './TaxonomyLookupModalContent.scss';

function TaxonomyLookupModalContent({
  suggestConfig,
  suggestInit,
  opModel,
  search,
  getTreeView,
  getSuggestions,
  searchByKeyword,
  showEmptyResult,
  startLoading,
  stopLoading,
  boundOperator,
  applyResultsFromTreeView,
  selectedItems,
  initTreeView,
  resetTreeView,
  clearSelected,
  selectBoundOperator,
}) {
  const [searchStatus, setSearchStatus] = useState('');
  const modalTitle = opModel?.title;
  // This modal is reused for other lookup types, so status/aria text must not
  // hardcode "taxonomy" — fall back to a generic term when no title is set.
  const statusPrefix = modalTitle || 'Lookup';
  // Only announce "search cleared" once the user has actually run a search —
  // the tree's initial load also reports an empty query, which must stay silent.
  const hasSearchedRef = useRef(false);
  const treeViewOptions = {
    onEmptyResult: (query) => {
      showEmptyResult();
      setSearchStatus(`No ${statusPrefix} matches found for "${query?.text || 'the current search'}".`);
    },
    onResults: ({ query, count }) => {
      if (!query?.text) {
        if (hasSearchedRef.current) setSearchStatus(`${statusPrefix} search cleared.`);
        return;
      }

      hasSearchedRef.current = true;
      setSearchStatus(`${count} ${statusPrefix} ${count === 1 ? 'match' : 'matches'} found for "${query.text}".`);
    },
    onLoadingStart: startLoading,
    onLoadingEnd: stopLoading,
  };

  const applyResults = (selectedTaxonomies, selectedCustom, totalHitCount) => {
    applyResultsFromTreeView(selectedTaxonomies, selectedCustom, totalHitCount);
  };

  const treeAriaLabel = `${statusPrefix} tree`;

  const handleGet = (params) => getTreeView(params, suggestInit);
  const operatorsDropdownOptions = Object.keys(TAXONOMY_BOUND_OPERATORS).map((operator) => ({
    label: TAXONOMY_BOUND_OPERATORS[operator],
    value: operator,
  }));
  const getInitialQuery = () => {
    const initialSearch = get(suggestInit, 'search');
    const query = {
      text: '',
      boundOperator,
    };
    if (!initialSearch || initialSearch.length < 2) {
      return query;
    }
    const terms = initialSearch.includes(';') ? initialSearch.split(';') : [initialSearch];
    if (terms.length === 1) {
      query.text = initialSearch;
    }
    return query;
  };

  return (
    <section className="taxonomyLookupModal__content">
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {searchStatus}
      </div>
      <TreeView
        taxonomyType={suggestConfig?.queryLet.taxonomy}
        taxonomiesGetter={handleGet}
        onChange={applyResults}
        options={treeViewOptions}
        initialQuery={getInitialQuery()}
        selectedCustomItems={selectedItems}
        onInit={initTreeView}
        onReset={resetTreeView}
        ariaLabel={treeAriaLabel}
        layout="sidebar"
        onClearSelected={clearSelected}
        treeControls={
          <div className="taxonomyLookupModal__treeControls">
            <SingleSelect
              id="taxonomyLookupModal__treeControls_dropdown"
              className="e2e-taxonomyLookupModal-tree-bound-operator-select"
              options={operatorsDropdownOptions}
              selectedOptions={[boundOperator || operatorsDropdownOptions[0]?.value]}
              label="Search term operators"
              data-e2e="taxonomyLookupModal-tree-bound-operator-select"
              required
              hideRequired
              onSelect={(option) => option && selectBoundOperator(option.value)}
              size="small"
            />
            <TaxonomyLookupModalSearch
              search={search}
              getSuggestions={getSuggestions}
              searchByKeyword={searchByKeyword}
              suggestInit={suggestInit}
            />
          </div>
        }
      />
    </section>
  );
}

TaxonomyLookupModalContent.propTypes = {
  suggestConfig: PropTypes.object,
  suggestInit: PropTypes.object,
  opModel: PropTypes.object,
  search: PropTypes.string,
  selectedItems: PropTypes.array,
  boundOperator: PropTypes.string,
  showEmptyResult: PropTypes.func.isRequired,
  startLoading: PropTypes.func.isRequired,
  stopLoading: PropTypes.func.isRequired,
  getTreeView: PropTypes.func.isRequired,
  getSuggestions: PropTypes.func.isRequired,
  searchByKeyword: PropTypes.func.isRequired,
  initTreeView: PropTypes.func.isRequired,
  applyResultsFromTreeView: PropTypes.func.isRequired,
  resetTreeView: PropTypes.func.isRequired,
  clearSelected: PropTypes.func.isRequired,
  selectBoundOperator: PropTypes.func.isRequired,
};

export default TaxonomyLookupModalContent;
